<template>
    <PhotosListControlsComponent
        :folder="folder"
        :selected="selected"
        @removeSelected="removeSelected"
    />

    <section v-if="isLoadingPhotos" class="pb-16 relative overflow-x-auto rounded-md mt-4">
        <!-- Table skeleton -->
        <div class="inline-block min-w-full pt-2 align-middle bg-white rounded-md">
            <table class="min-w-full divide-y divide-gray-300 rounded-md animate-pulse">
                <thead>
                    <tr>
                        <th class="px-3 py-3"><div class="skeleton rounded h-4 w-4"></div></th>
                        <th class="px-3 py-3"><div class="skeleton rounded h-4 w-24"></div></th>
                        <th class="px-3 py-3"><div class="skeleton rounded h-4 w-16"></div></th>
                        <th class="px-3 py-3"><div class="skeleton rounded h-4 w-16"></div></th>
                        <th class="px-3 py-3"><div class="skeleton rounded h-4 w-20"></div></th>
                        <th class="relative py-3 pr-4 pl-3"><div class="skeleton rounded h-4 w-8 ml-auto"></div></th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white rounded-md">
                    <tr class="cursor-default" v-for="i in 6" :key="`skeleton-row-${i}`">
                        <td class="px-3 py-3"><div class="skeleton rounded-full w-10 h-10"></div></td>
                        <td class="px-3 py-3"><div class="skeleton rounded h-4 w-24"></div></td>
                        <td class="px-3 py-3"><div class="skeleton rounded h-4 w-16"></div></td>
                        <td class="px-3 py-3"><div class="skeleton rounded h-4 w-16"></div></td>
                        <td class="px-3 py-3"><div class="skeleton rounded h-4 w-20"></div></td>
                        <td class="relative py-3 pr-4 pl-3 text-right"><div class="skeleton rounded h-6 w-20 ml-auto"></div></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>

    <section v-if="!isLoadingPhotos" :class="{'pb-16 relative overflow-x-auto rounded-md': true, 'mt-4': selected.length === 0}" ref="photosContainer">
        <div class="w-full flex items-center justify-center text-center mx-auto mt-8 my-4 text-gray-600" v-if="photos.length === 0">
            <span class="text-muted" v-if="!isTrashedFolder && folder">
                {{ $t('files.empty.folders_1') }} <br />{{ $t('files.empty.folders_2') }}
            </span>
            <span class="text-muted" v-if="!folder && !isTrashedFolder">
                {{ $t('files.empty.files') }}
            </span>
            <span class="text-muted" v-if="isTrashedFolder">
                {{ $t('files.empty.trashed') }}
            </span>
        </div>

        <div v-if="photos.length !== 0" class="inline-block min-w-full pt-2 align-middle bg-white rounded-md">
            <table class="min-w-full divide-y divide-gray-300 rounded-md">
                <thead>
                    <tr>
                        <th scope="col" class="px-3 py-3 text-left text-sm font-semibold text-gray-900"></th>
                        <th scope="col" class="px-3 py-3 text-left text-sm font-semibold text-gray-900">{{ $t('files.list.name') }}</th>
                        <th scope="col" class="px-3 py-3 text-left text-sm font-semibold text-gray-900">{{ $t('files.list.type') }}</th>
                        <th scope="col" class="px-3 py-3 text-left text-sm font-semibold text-gray-900">{{ $t('files.list.size') }}</th>
                        <th scope="col" class="px-3 py-3 text-left text-sm font-semibold text-gray-900">{{ isTrashedFolder ? $t('files.list.deleted') : $t('files.list.folders') }}</th>
                        <th scope="col" class="relative py-3 pr-4 pl-3 sm:pr-0"></th>
                    </tr>
                </thead>
                <draggable
                    v-model="sortedPhotos"
                    @start="drag=true"
                    @end="drag=false"
                    item-key="id"
                    tag="tbody"
                    role="list"
                    :class="{'divide-y divide-gray-200 bg-white rounded-md': true}"
                    :disabled="isSelecting">
                    <template #item="{element, index}">
                        <tr @click.prevent="select(element, index, $event)"
                            @contextmenu.prevent="showContextMenuForPhoto(element, $event)"
                            @mouseenter="onVideoMouseEnter($event)"
                            @mouseleave="onVideoMouseLeave($event)"
                            :class="{
                                'photo-list-item rounded-md cursor-pointer': true,
                                'bg-royal-blue-50': isSelectedPhoto(element),
                                'hover:bg-gray-100': !isSelectedPhoto(element),
                                'bg-gray-50': element.is_compressing,
                            }"
                            :ref="`photo-${index}`">
                            <td class="py-3 pr-3 pl-4 text-sm whitespace-nowrap w-[1%] relative">
                                <div v-if="element.is_compressing" class="absolute inset-y-0 left-0 w-0.5 bg-black/50"></div>
                                <div v-if="isSelectedPhoto(element)" class="absolute inset-y-0 left-0 w-0.5 bg-royal-blue-600"></div>
                                <div class="size-11 shrink-0 relative">
                                    <div v-if="element.is_compressing" class="size-11 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                        <svg class="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                                        </svg>
                                    </div>
                                    <video v-if="!element.is_compressing && element.is_video && element.compressed_video_url" class="size-11 rounded-full object-cover" preload="metadata" playsinline muted :poster="element.preview_mini_url" loop>
                                        <source :src="element.compressed_video_url" type="video/mp4">
                                        Your browser does not support the video tag.
                                    </video>
                                    <img
                                        v-if="!element.is_compressing && (!element.is_video || element.compressed_video_url === null)"
                                        class="size-11 rounded-full object-cover" :src="element.preview_mini_url"
                                        :alt="element.original_filename"
                                        @error="handleImageError"
                                    />
                                    
                                    <EyeSlashIcon 
                                        v-if="element.is_hidden_by_client" 
                                        class="absolute -top-1 -right-1 text-white bg-gray-600 p-0.5 rounded-full size-5"
                                    />

                                    <StarIcon v-if="element.is_cover" class="absolute -top-1 -left-1 text-white bg-gray-600 p-0.5 rounded-full size-5" />
                                </div>
                            </td>
                            <td class="px-3 py-3 text-sm whitespace-nowrap text-gray-500">
                                <div class="text-gray-900">{{ element.original_filename }}</div>
                            </td>
                            <td v-if="element.is_compressing" class="px-3 py-3 text-sm whitespace-nowrap text-gray-500" colspan="2">
                                <div class="text-gray-900">{{ $t('files.file_is_compressing') }}</div>
                            </td>
                            <td v-if="!element.is_compressing" class="px-3 py-3 text-sm whitespace-nowrap text-gray-500">
                                <div class="text-gray-900">
                                    {{ element.is_video ? $t('files.types.video') : $t('files.types.photo') }}
                                </div>
                            </td>
                            <td v-if="!element.is_compressing" class="px-3 py-3 text-sm whitespace-nowrap text-gray-500">
                                <div class="text-gray-900">{{ formatStorage(element.filesize, $t) }}</div>
                            </td>
                            <td v-if="!isTrashedFolder" class="px-3 py-3 text-sm whitespace-nowrap text-gray-500">
                                <div class="flex items-center">
                                    <span
                                        v-for="(_folder, folderIndex) in element.folders"
                                        :key="`folder-photo-label-${element.id}-${_folder.id}-${folderIndex}`"
                                        class="ml-1"
                                    >
                                        <svg class="size-1.5" :style="`fill: ${_folder.color}` as any" viewBox="0 0 6 6" aria-hidden="true">
                                          <circle cx="3" cy="3" r="3" />
                                        </svg>
                                    </span>
                                </div>
                            </td>
                            <td v-if="isTrashedFolder" class="px-3 py-3 text-sm whitespace-nowrap text-gray-500">
                                <div class="text-gray-900">{{ element.deleted_at }}</div>
                            </td>
                            <td class="relative py-3 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap">
                                <div class="flex gap-2 justify-end">
                                    <button
                                        v-if="isTrashedFolder"
                                        @click.prevent="recoverItem(element)"
                                        :disabled="isRecoveringDeletedItem"
                                        type="button"
                                        class="text-xs font-normal text-white bg-royal-blue-500 p-1.5 rounded-full hover:bg-royal-blue-600 focus:outline-none cursor-pointer flex items-center gap-1">
                                        <ArrowUturnLeftIcon class="size-4" />
                                    </button>
                                    <button
                                        @click.prevent="deleteItem(element)"
                                        :disabled="isDeletingPhoto"
                                        type="button"
                                        class="text-xs font-normal text-white bg-red-600 px-2 py-1.5 rounded-full hover:bg-red-700 focus:outline-none cursor-pointer flex items-center gap-1">
                                        <TrashIcon class="size-4" />

                                        {{ $t('files.delete_button') }}
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </template>
                </draggable>
            </table>
        </div>

        <div class="selection-box" v-if="isSelecting" :style="selectionBoxStyle"></div>
    </section>

    <DeletePhotosConfirmationModalComponent
        :open="isDeletingConfirmation !== null"
        @close="isDeletingConfirmation = null"
        @confirmAction="deleteItem(isDeletingConfirmation)"
        :title="$t('files.delete_confirmation_title')"
        :message="isTrashedFolder ? $t('files.delete_confirmation_message') : $t('files.delete_confirmation_trash')"
        :buttonText="$t('files.delete_confirmation_button')"
        :isLoading="isDeletingPhoto"
    />

    <ContextMenuComponent
        :photo="contextMenuPhoto"
        :folders="allFolders"
        :is-visible="showContextMenu"
        :position="contextMenuPosition"
        :is-trashed-folder="isTrashedFolder"
        @close="closeContextMenu"
        @delete="handleContextDelete"
        @recover="handleContextRecover"
        @add-to-scene="handleAddToScene"
        @make-cover="handleMakeCover"
        @click.stop
    />
</template>

<script lang="ts">
import { defineComponent } from "vue";
import draggable from "vuedraggable";
import { Photo } from "@/types/PhotoType";
import { mapActions, mapGetters } from "vuex";
import PhotosListControlsComponent from "@/components/Galleries/PhotosListControlsComponent.vue";
import {formatStorage} from "@/_helpers/formatters";
import {TrashIcon, ArrowUturnLeftIcon, EyeSlashIcon, StarIcon} from "@heroicons/vue/24/outline";
import DeletePhotosConfirmationModalComponent from "@/components/Galleries/Modals/DeletePhotosConfirmationModalComponent.vue";
import ContextMenuComponent from "@/components/Galleries/ContextMenuComponent.vue";

export default defineComponent({
    name: "PhotosListTableComponent",

    components: {
        StarIcon,
        DeletePhotosConfirmationModalComponent,
        ContextMenuComponent,
        TrashIcon,
        ArrowUturnLeftIcon,
        PhotosListControlsComponent,
        draggable,
        EyeSlashIcon
    },

    props: {
        layout: Number,
        folder: Object,
        smallGrid: Boolean,
    },

    data() {
        return {
            selected: [] as Photo[],
            lastSelectedIndex: null as number | null,
            drag: false,
            isSelecting: false,
            selectionStart: { x: 0, y: 0 },
            selectionEnd: { x: 0, y: 0 },
            isSelectMode: false,

            isDeletingConfirmation: null as Photo | null,

            // Context menu
            showContextMenu: false,
            contextMenuPhoto: null as Photo | null,
            contextMenuPosition: { x: 0, y: 0 },
        };
    },

    computed: {
        ...mapGetters({
            photos: "photos/photos",
            gallery: "galleries/gallery",
            folders: "galleries/folders",
            isLoadingPhotos: "photos/isLoadingList",
            isDeletingPhoto: "photos/isDeletingItem",
            isRecoveringDeletedItem: "photos/isRecoveringDeletedItem",
        }),

        isTrashedFolder(): boolean {
            return (this.folder != null) && this.folder?.slug == 'trash';
        },

        allFolders() {
            const folders = this.folders as any[];
            return folders.filter((folder: any) => folder.slug !== 'trash');
        },

        sortedPhotos: {
            get(): Photo[] {
                return this.photos;
            },
            set(value: Photo[]) {
                this.sortPhotos({
                    photos: value,
                    gallery: this.gallery.slug,
                    type: "manual",
                    folder: this.folder?.slug,
                });

                this.$emit("photo-order-manually");
            },
        },

        gridCol(): Number {
            if(this.smallGrid) {
                return 2;
            }

            switch (this.layout) {
                case 2:
                    return 6;
                case 4:
                    return 3;
                case 5:
                    return 2;
                default:
                    return 4;
            }
        },

        selectionBoxStyle(): object {
            const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
            const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);
            const left = Math.min(this.selectionStart.x, this.selectionEnd.x) + "px";
            const top = Math.min(this.selectionStart.y, this.selectionEnd.y) + "px";

            return {
                width: width + "px",
                height: height + "px",
                left,
                top,
                position: "absolute",
                border: "2px dashed #3498db",
                backgroundColor: "rgba(52, 152, 219, 0.2)",
                zIndex: 100,
            };
        },
    },

    methods: {
        formatStorage,
        ...mapActions({
            deletePhoto: "photos/deletePhoto",
            deleteTrashedPhoto: "photos/deleteTrashedPhoto",
            recoverDeletedPhoto: "photos/recoverDeletedPhoto",
            sortPhotos: "photos/sortPhotos",
            coverPhoto: "photos/coverPhoto",
            moveToFolder: "photos/moveToFolder",

            loadGallery: "galleries/loadGallery",

            successAlert: "alerts/success",
            errorAlert: "alerts/error",
        }),

        onKeyDown(event: KeyboardEvent) {
            if (event.ctrlKey || event.metaKey) {
                this.isSelectMode = true;
            }

            if(event.key === "Escape") {
                this.isSelectMode = false;
                this.selected = [];
                this.closeContextMenu();
            }
        },

        onKeyUp(event: KeyboardEvent) {
            if (!event.ctrlKey && !event.metaKey) {
                this.isSelectMode = false;
            }
        },

        startSelection(event: MouseEvent) {
            if (!this.isSelectMode) return;

            const container = this.$refs.photosContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();

            this.isSelecting = true;
            this.selectionStart = {
                x: event.clientX - containerRect.left,
                y: event.clientY - containerRect.top
            };
            this.selectionEnd = { ...this.selectionStart };

            window.addEventListener("mousemove", this.updateSelection);
            window.addEventListener("mouseup", this.endSelection);
            event.preventDefault();
        },

        updateSelection(event: MouseEvent) {
            const container = this.$refs.photosContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();

            this.selectionEnd = {
                x: event.clientX - containerRect.left,
                y: event.clientY - containerRect.top
            };

            this.updateSelectedPhotos();
        },

        updateSelectedPhotos() {
            const container = this.$refs.photosContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();

            const selectionRect = {
                left: Math.min(this.selectionStart.x, this.selectionEnd.x),
                top: Math.min(this.selectionStart.y, this.selectionEnd.y),
                right: Math.max(this.selectionStart.x, this.selectionEnd.x),
                bottom: Math.max(this.selectionStart.y, this.selectionEnd.y),
            };

            const photoElements = container.querySelectorAll('.photo-list-item');

            this.selected = this.sortedPhotos.filter((photo, index) => {
                const photoElement = photoElements[index] as HTMLElement;
                if (!photoElement) return false;

                const photoRect = photoElement.getBoundingClientRect();

                const relativePhotoRect = {
                    left: photoRect.left - containerRect.left,
                    right: photoRect.right - containerRect.left,
                    top: photoRect.top - containerRect.top,
                    bottom: photoRect.bottom - containerRect.top,
                };

                return !(
                    selectionRect.right < relativePhotoRect.left ||
                    selectionRect.left > relativePhotoRect.right ||
                    selectionRect.bottom < relativePhotoRect.top ||
                    selectionRect.top > relativePhotoRect.bottom
                );
            });
        },

        endSelection() {
            this.isSelecting = false;
            window.removeEventListener("mousemove", this.updateSelection);
            window.removeEventListener("mouseup", this.endSelection);
        },

        select(photo: Photo, index: number, event: MouseEvent) {
            if (event.shiftKey && this.lastSelectedIndex !== null) {
                const range = [this.lastSelectedIndex, index].sort((a, b) => a - b);
                const photosToSelect = this.sortedPhotos.slice(range[0], range[1] + 1);

                photosToSelect.forEach((photoInRange) => {
                    if (!this.isSelectedPhoto(photoInRange)) {
                        this.selected.push(photoInRange);
                    }
                });
            } else {
                if (this.isSelectedPhoto(photo)) {
                    this.selected = this.selected.filter((item) => item.id !== photo.id);
                } else {
                    this.selected.push(photo);
                }
                this.lastSelectedIndex = index;
            }
        },

        isSelectedPhoto(photo: Photo): boolean {
            return !!this.selected.find((item) => item.id === photo.id);
        },

        deleteItem(photo: Photo) {
            if (this.isDeletingPhoto) return;

            if(this.isDeletingConfirmation === null) {
                this.isDeletingConfirmation = photo;
                return;
            }

            if (this.isTrashedFolder) {
                this.deleteTrashedPhoto({photo})
                    .then(() => {
                        this.isDeletingConfirmation = null;
                        this.isDeletingPhoto = false;
                        this.selected = [];
                        this.successAlert({message: this.$t('files.deleted_success')});
                    })
                    .catch(() => {
                        this.isDeletingConfirmation = null;
                        this.isDeletingPhoto = false;
                        this.selected = [];
                        this.errorAlert({message: this.$t('files.deleted_error')});
                    });

                return;
            }

            this.deletePhoto({photo})
                .then(() => {
                    this.isDeletingConfirmation = null;
                    this.isDeletingPhoto = false;
                    this.selected = [];
                    this.successAlert({message: this.$t('files.deleted_success')});
                })
                .catch(() => {
                    this.isDeletingConfirmation = null;
                    this.isDeletingPhoto = false;
                    this.selected = [];
                    this.errorAlert({message: this.$t('files.deleted_error')});
                });
        },

        recoverItem(photo: Photo) {
            if (this.isRecoveringDeletedItem) return;

            this.recoverDeletedPhoto({photo})
                .then(() => {
                    this.successAlert({message: this.$t('files.restored_success')});
                    this.selected = [];
                })
                .catch(({data}) => {
                    this.selected = [];

                    if(data?.message) {
                        this.errorAlert({
                            message: data.message,
                        });
                        return;
                    }

                    this.errorAlert({
                        message: this.$t('files.restored_error', {
                            message: data.message,
                        }),
                    });
                });
        },

        removeSelected(photos: Photo[] = []) {
            if (photos.length == 0) {
                this.selected = [];
            }
        },

        onVideoMouseEnter(event: MouseEvent) {
            const video = (event.currentTarget as HTMLElement).querySelector('video');
            if (video) {
                video.play().catch(() => {
                    //
                });
            }
        },

        onVideoMouseLeave(event: MouseEvent) {
            const video = (event.currentTarget as HTMLElement).querySelector('video');
            if (video) {
                video.pause();
                video.currentTime = 0;
            }
        },

        handleImageError(event: Event) {
            const target = event.target as HTMLImageElement;
            if (target) {
                target.src = require(`@/assets/images/img-error.jpg`);
            }
        },

        // Context menu methods
        showContextMenuForPhoto(photo: Photo, event: MouseEvent) {
            this.contextMenuPhoto = photo;
            this.contextMenuPosition = {
                x: event.clientX,
                y: event.clientY
            };
            this.showContextMenu = true;
        },

        closeContextMenu() {
            this.showContextMenu = false;
            this.contextMenuPhoto = null;
        },

        handleDocumentClick(event: MouseEvent) {
            // Додаємо затримку, щоб дати можливість кнопкам в меню спрацювати першими
            setTimeout(() => {
                // Перевіряємо, чи клік не на фото (рядок таблиці) і не на контекстне меню
                const target = event.target as HTMLElement;
                const photoRow = target.closest('tr.photo-list-item');
                const contextMenu = target.closest('.context-menu');
                const contextMenuContainer = target.closest('[ref="contextMenu"]');
                
                if (!photoRow && !contextMenu && !contextMenuContainer && this.showContextMenu) {
                    this.closeContextMenu();
                }
            }, 0);
        },

        handleContextDelete(photo: Photo) {
            if (photo) {
                this.deleteItem(photo);
            }
        },

        handleContextRecover(photo: Photo) {
            if (photo) {
                this.recoverItem(photo);
            }
        },

        handleAddToScene({ photo, folder }: { photo: Photo; folder: any }) {
            if (photo && folder) {
                this.handleSceneSelected({ photo, folder });
            }
        },

        handleMakeCover(photo: Photo) {
            if (photo) {
                this.setCover(photo);
            }
        },

        handleSceneSelected({ photo, folder }: { photo: Photo; folder: any }) {
            if (photo && folder) {
                this.moveToFolder({photos: [photo], folder: folder.slug})
                    .then(() => {
                        this.successAlert({ 
                            message: this.$t('files.context_menu.add_to_scene') + ' - ' + folder.title 
                        });
                        // Оновлюємо список фото після переміщення
                        this.$emit('photo-moved');
                    })
                    .catch(({data}) => {
                        this.errorAlert({
                            message: this.$t('files.error.files', {
                                message: data?.message || 'Помилка при додаванні до сцени'
                            }),
                        });
                    });
            }
        },

        setCover(photo: Photo) {
            if (this.isDeletingPhoto || !photo) return;

            this.coverPhoto({photo})
                .then(() => {
                    this.successAlert({message: this.$t('files.cover_set_success')});

                    this.loadGallery({slug: this.gallery.slug})
                        .catch(() => {
                            this.errorAlert({
                                message: this.$t('files.cover_update_error'),
                            });
                        });
                })
                .catch(({data}) => {
                    this.selected = [];

                    if(data?.message) {
                        this.errorAlert({
                            message: data.message,
                        });
                        return;
                    }

                    this.errorAlert({
                        message: this.$t('files.cover_set_error', { message: data?.message }),
                    });
                });
        },
    },

    mounted() {
        document.addEventListener("keydown", this.onKeyDown);
        document.addEventListener("keyup", this.onKeyUp);
        document.addEventListener("mousedown", this.startSelection);
        document.addEventListener("click", this.handleDocumentClick);
    },

    beforeUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
        document.removeEventListener("keyup", this.onKeyUp);
        document.removeEventListener("mousedown", this.startSelection);
        document.removeEventListener("click", this.handleDocumentClick);
    },
});
</script>

<style lang="scss" scoped>
.photos-list-container {
    position: relative;

    .gallery-photo-item {
        .card-img {
            display: block;
            width: 100%;
            height: 200px;
            max-width: 100%;
            max-height: 100%;
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
        }
    }

    .photo-list-item.col-2 {
        .gallery-photo-item {
            .card-img {
                height: 120px;
            }

            .gallery-photo-in-job-disabled {
                font-size: 12px;
            }
        }
    }

    .photo-list-item.col-4:nth-last-child(-n + 3) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }

    .photo-list-item.col-3:nth-last-child(-n + 4) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }

    .photo-list-item.col-2:nth-last-child(-n + 5) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }

    .photo-list-item.col-6:nth-last-child(-n + 2) {
        .gallery-photo-item {
            margin-bottom: 0;
        }
    }
}

.selection-box {
    position: absolute;
    border: 2px dashed #3498db;
    background-color: rgba(52, 152, 219, 0.2);
    z-index: 100;
}

.is-selecting-container {
    cursor: crosshair;
}
</style>